import React, { useMemo } from 'react';
import { Moment } from 'moment';
import { omit } from 'lodash';
import { FieldArray, WrappedFieldArrayProps, FieldArrayFieldsProps } from 'redux-form';

import IntervalsListInput from '@common/components/intervals-list-input';
import { getNewEntry } from '@common/components/intervals-list-input/utils';

export type NotificationType = {
  id: string;
  value: string;
  unit: 'minutes' | 'hours' | 'days';
};

type FieldProps = {
  fields: FieldArrayFieldsProps<NotificationType>
  startDate: Moment;
  startTime: string;
  startIsFullDay: boolean;
  disabled: boolean;
};

const baseTime = { hour: 0, minute: 0, second: 0, millisecond: 0 };

const getMaxByUnit = (minutes: number, unit: NotificationType['unit']) => {
  let max = minutes;
  if (unit === 'hours' || unit === 'days') {
    max /= 60;
  }
  if (unit === 'days') {
    max /= 24;
  }
  return Math.floor(max);
};

const getMaxValue = (start: Moment, unit: NotificationType['unit']) => {
  const now = (new Date()).getTime();
  const diffMinutes = (start.valueOf() - now) / 1000 / 60;
  const max = getMaxByUnit(diffMinutes, unit);
  return max > 0 ? max : 0;
};

const Input = ({
  startDate,
  startTime,
  startIsFullDay,
  disabled,
  fields,
}: FieldProps) => {
  const start = useMemo(() => {
    const [hours, minutes] = startTime.split(':');
    startDate.set({ ...baseTime, hour: parseInt(hours), minute: parseInt(minutes) });
    return startDate;
  }, [startDate, startTime, startIsFullDay]);

  const entries = useMemo(() => {
    return fields.map((name, index) => {
      const entry = fields.get(index);
      const max = getMaxValue(start, entry.unit);
      let newValue = entry.value;
      if (parseInt(entry.value) > max) {
        newValue = `${max}`;
        fields.splice(index, 1, {
          ...entry,
          value: newValue,
        });
      }
      return { id: entry.id, max, name };
    });
  }, [start, fields]);

  const onAdd = () => {
    fields.push(getNewEntry('', 'hours'));
  };

  const onDelete = (idToDelete: string) => {
    const index = fields.getAll().findIndex(({ id }) => id === idToDelete);
    fields.remove(index);
  };

  return (
    <IntervalsListInput
      entries={entries}
      onDelete={onDelete}
      onAdd={onAdd}
      disabled={disabled}
    />
  );
};

const ReduxFormField = (props: WrappedFieldArrayProps<any> & FieldProps) => (
  <Input {...omit(props, ['meta'])} />
);

const NotificationsInput = ({ name, ...props }: Omit<FieldProps, 'fields'> & { name: string }) => (
  <FieldArray
    name={name}
    component={ReduxFormField}
    props={props}
  />
);

export default NotificationsInput;
